import React, { FC, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { getQueryParam, setQueryParams } from '../../helpers/url/urlHelpers';
import { IAccordion, IAccordionItem } from '../../interfaces/umbracoNodes/contentSlices';
import { ContentContainer } from '../base/contentContainer/contentContainer';
import {
	AccordionBlock,
	AccordionContainer,
	AccordionImage,
	AccordionItemTitle,
	AccordionLine,
	AccordionTrigger,
	ButtonStyled,
	DetailsBlock,
	IconWrapper,
	Line1,
	Line2,
	RichTextConditionalStyled,
	RichTextStyled,
	RichTextTitleStyled,
	Root,
} from './accordion.style';

export const AccordionBlockComponent = ({ accordionItems, activeItem, onTriggerClick, hasImage }) => {
	return (
		<AccordionBlock hasImage={hasImage}>
			{accordionItems.map(
				(item, idx) =>
					item.Values && (
						<AccordionLine id={item.Values.Query} key={idx}>
							<AccordionTrigger
								active={activeItem === item.Values.Query}
								onClick={() => onTriggerClick(item.Values.Query)}>
								<AccordionItemTitle>{item.Values.Title}</AccordionItemTitle>
								<IconWrapper>
									<Line1 />
									<Line2 />
								</IconWrapper>
							</AccordionTrigger>
							<AnimateHeight duration={300} height={activeItem === item.Values.Query ? 'auto' : 0}>
								<RichTextStyled content={item.Values.Description} isSmall />
							</AnimateHeight>
						</AccordionLine>
					)
			)}
		</AccordionBlock>
	);
};

interface Props {
	content: IAccordion;
}

const Accordion: FC<Props> = ({ content }) => {
	const { Title, Description, Image, Button, Items } = content.Values;
	const accordionItems: IAccordionItem[] = Items.map(item => ({
		Values: {
			...item.Values,
			Query: item.Values.Query.replace(/\s+/g, '-')
				.replace(/[^a-zA-Z0-9-+_]/g, '')
				.toLowerCase(),
		},
	}));
	const query = getQueryParam('content');
	const [activeItem, setActiveItem] = useState<string>(query);

	useEffect(() => {
		const scrollToSelectedAccordion = () => {
			if (!query || !accordionItems.find(item => item.Values.Query === query)) {
				return;
			}
			const top = document.getElementById(query).getBoundingClientRect().top + window.scrollY;
			window.scroll({
				top: top,
				behavior: 'smooth',
			});
		};
		window.addEventListener('load', scrollToSelectedAccordion);
		return () => {
			window.removeEventListener('load', scrollToSelectedAccordion);
		};
	}, [accordionItems, query]);

	const onTriggerClick = (query) => {
		setActiveItem(query === activeItem ? null : query);
		setQueryParams({ content: query });
	};

	return (
		<Root>
			<ContentContainer>
				<AccordionContainer horizontal={!!Description || !!Button || !!Image} hasImage={!!Image}>
					<DetailsBlock>
						<RichTextTitleStyled content={Title} type="h3" />
						{Description && <RichTextConditionalStyled content={Description} />}
						{Image && (
							<AccordionBlockComponent
								accordionItems={accordionItems}
								activeItem={activeItem}
								onTriggerClick={onTriggerClick}
								hasImage={!!Image}
							/>
						)}
						{Button && <ButtonStyled title={Button.Name} href={Button.Url} target={Button.Target} />}
					</DetailsBlock>

					{!Image && (
						<AccordionBlockComponent
							accordionItems={accordionItems}
							activeItem={activeItem}
							onTriggerClick={onTriggerClick}
							hasImage={!!Image}
						/>
					)}
					{Image && <AccordionImage src={Image.Src} sizes="(max-width: 700px), (max-height: 550px)" />}
				</AccordionContainer>
			</ContentContainer>
		</Root>
	);
};

export default Accordion;
